// Apolo IDE — E2-T02 · TracePanel.
//
// A READ-ONLY, durable, after-the-fact TRACE view for a Code agent run. The live
// UX (LiveActivity / ToolCallBlock) is ephemeral; this panel answers "which turn
// was slow or token-heavy?" AFTER the run by reading session_events via
// useSessionTrace → buildTrace.
//
//   • Header: run totals — Σ tokens (in/out), Σ duração, # turns, # errors.
//   • List: one row per turn — index · model · duração · tokens in/out · ok/error.
//   • States: loading / error / empty all handled (degrades gracefully).
//
// Liquid-glass aesthetic via the `rx-trace*` classes in redesign.css (glass +
// accent blue; color only on the ok/error badges). Never writes — pure render.

import { useState, useId } from 'react';
import { Loader2, AlertTriangle, GitCommitVertical, CheckCircle2, XCircle } from 'lucide-react';
import './redesign.css';
import { useSessionTrace } from '../../../hooks/useSessionTrace.js';

function fmtDuration(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms / 1000)}s`;
}

function fmtTokens(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k`;
}

function Stat({ label, value, tone }) {
  return (
    <span className="rx-trace-stat" data-tone={tone || undefined}>
      <span className="rx-trace-stat-k">{label}</span>
      <span className="rx-trace-stat-v">{value}</span>
    </span>
  );
}

function TraceTotals({ totals }) {
  return (
    <div className="rx-trace-totals" role="group" aria-label="Totais do run">
      <Stat label="Turns" value={totals.turnCount} />
      <Stat label="Tokens in" value={fmtTokens(totals.totalTokensIn)} />
      <Stat label="Tokens out" value={fmtTokens(totals.totalTokensOut)} />
      <Stat label="Σ tokens" value={fmtTokens(totals.totalTokens)} />
      <Stat label="Σ duração" value={fmtDuration(totals.totalDurationMs)} />
      <Stat
        label="Erros"
        value={totals.errorCount}
        tone={totals.errorCount > 0 ? 'error' : undefined}
      />
    </div>
  );
}

/**
 * E5 (S3 Glass Box) — read-only audit section shown per turn when a
 * steering_spec event was recorded for that turn. Collapses the spec details
 * (requirements / acceptance) behind an accessible toggle. Audit-only.
 */
function SteeringSpecSection({ steeringSpec }) {
  const [open, setOpen] = useState(false);
  // useId produces a stable, unique id per component instance — no recalculation
  // on re-render and no collision between multiple SteeringSpecSection instances.
  const reactId = useId();
  const panelId = `ss-panel-${reactId.replace(/:/g, '')}`;

  const files = Array.isArray(steeringSpec.steeringFiles) ? steeringSpec.steeringFiles : [];
  const spec = steeringSpec.specPresent && steeringSpec.spec ? steeringSpec.spec : null;
  const accCount = Array.isArray(spec?.acceptance) ? spec.acceptance.length : 0;

  return (
    <section
      className="rx-trace-steering-spec"
      aria-label="Steering e Spec do turno"
    >
      {files.length > 0 && (
        <ul className="rx-trace-steering-files" aria-label="Arquivos de steering">
          {files.map((f, i) => (
            <li key={i} className="rx-trace-steering-file">
              <span className="rx-trace-steering-kind">{f.kind || 'workflow'}</span>
              <span className="rx-trace-steering-path" title={f.path}>{f.path || '—'}</span>
            </li>
          ))}
        </ul>
      )}
      {spec && (
        <div className="rx-trace-spec-summary">
          <button
            type="button"
            className="rx-trace-spec-toggle"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((v) => !v)}
          >
            {`Spec — ${accCount} critério${accCount !== 1 ? 's' : ''} de aceitação`}
            <span aria-hidden="true">{open ? ' ▲' : ' ▼'}</span>
          </button>
          {/* Panel is always in the DOM so aria-controls always points to a real node.
              Visibility is toggled via the `hidden` attribute (hides + removes from a11y tree). */}
          <div id={panelId} className="rx-trace-spec-detail" hidden={!open}>
            {Array.isArray(spec.requirements) && spec.requirements.length > 0 && (
              <div className="rx-trace-spec-block">
                <b>Requirements</b>
                <ul>
                  {spec.requirements.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(spec.acceptance) && spec.acceptance.length > 0 && (
              <div className="rx-trace-spec-block">
                <b>Acceptance</b>
                <ul>
                  {spec.acceptance.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function TurnRow({ turn, index }) {
  const okLabel = turn.ok ? 'OK' : (turn.error || 'erro');
  return (
    <li className="rx-trace-row">
      <span className="rx-trace-idx" aria-hidden="true">{index + 1}</span>
      <span className="rx-trace-main">
        <span className="rx-trace-model" title={turn.model_id || 'modelo desconhecido'}>
          {turn.model_id || 'modelo desconhecido'}
        </span>
        <span className="rx-trace-meta">
          <span>⧗ <b>{fmtDuration(turn.duration_ms)}</b></span>
          <span>in <b>{fmtTokens(turn.tokens_in)}</b></span>
          <span>out <b>{fmtTokens(turn.tokens_out)}</b></span>
        </span>
      </span>
      <span
        className="rx-trace-badge"
        data-ok={turn.ok ? 'true' : 'false'}
        title={turn.ok ? 'Turno concluído' : `Erro: ${turn.error || 'desconhecido'}`}
      >
        {turn.ok ? (
          <CheckCircle2 size={12} aria-hidden="true" />
        ) : (
          <XCircle size={12} aria-hidden="true" />
        )}
        {okLabel}
      </span>
      {/* E2 (library-uplift) — pill discreta quando auto-skill foi injetada no prompt deste turno */}
      {Array.isArray(turn.skillsActivated) && turn.skillsActivated.length > 0 && (
        <span
          className="rx-trace-meta"
          title={`Skills auto-injetadas no prompt: ${turn.skillsActivated.join(', ')}`}
          style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}
        >
          {turn.skillsActivated.map((name) => (
            <span
              key={name}
              style={{
                fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 8,
                border: '1px solid var(--apolo-border-subtle, rgba(127,127,127,.28))',
                color: 'var(--apolo-text-tertiary, #8a8a86)', whiteSpace: 'nowrap',
              }}
            >
              ✦ {name}
            </span>
          ))}
        </span>
      )}
      {/* E5 — audit-only steering+spec section, shown only when recorded */}
      {turn.steeringSpec && (
        <SteeringSpecSection steeringSpec={turn.steeringSpec} />
      )}
    </li>
  );
}

/**
 * @param {{ sessionId: string, enabled?: boolean }} props
 */
export default function TracePanel({ sessionId, enabled = true }) {
  const { trace, loading, error, refetch } = useSessionTrace(sessionId, { enabled });

  if (loading && trace.turns.length === 0) {
    return (
      <div className="rx-trace" data-testid="trace-panel">
        <div className="rx-trace-loading" role="status" aria-live="polite">
          <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          Carregando trace…
        </div>
      </div>
    );
  }

  if (error && trace.turns.length === 0) {
    return (
      <div className="rx-trace" data-testid="trace-panel">
        <div className="rx-trace-error" role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>Não foi possível carregar o trace.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rx-trace-retry text-apolo-accent underline-offset-2 hover:underline"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (trace.turns.length === 0) {
    return (
      <div className="rx-trace" data-testid="trace-panel">
        <div className="rx-trace-empty" role="status">
          <GitCommitVertical size={14} aria-hidden="true" />
          Nenhum turno registrado ainda para esta sessão.
        </div>
      </div>
    );
  }

  return (
    <div className="rx-trace" data-testid="trace-panel" aria-label="Trace de execução do agente">
      <TraceTotals totals={trace.totals} />
      <ol className="rx-trace-list">
        {trace.turns.map((turn, i) => (
          <TurnRow key={turn.id || `${turn.started_at}-${i}`} turn={turn} index={i} />
        ))}
      </ol>
    </div>
  );
}
